import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";
import CustomYAxisTick from "./CustomYAxisTick";
import { useGetTopPerformingCampaigns } from "../../hooks/useGetDashboardApi";

function computeDomainMax(data) {
  if (!data.length) return 100;
  const max = Math.max(...data.map((d) => d.sent + d.opened + d.replied));
  if (max === 0) return 100;
  return Math.ceil((max * 1.08) / 10) * 10;
}

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;

  const data = [
    { name: "Sent", value: payload[2]?.value ?? 0, color: "#A6CBD5" },
    { name: "Opened", value: payload[1]?.value ?? 0, color: "#63A3B6" },
    { name: "Replied", value: payload[0]?.value ?? 0, color: "#207C97" },
  ];

  return (
    <div
      style={{
        background: "#0B0B0B",
        borderRadius: "12px",
        padding: "12px 14px",
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
        color: "#FFFFFF",
        fontSize: "13px",
        minWidth: "160px",
      }}
    >
      {data.map((item, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: i !== data.length - 1 ? "6px" : 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: item.color,
              }}
            />
            <span style={{ color: "#E5E7EB" }}>{item.name}:</span>
          </div>
          <span style={{ fontWeight: 600 }}>{item.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function TopPerformingCampaigns({ days = 30 }) {
  const { data: topPerformingCampaignsData } =
    useGetTopPerformingCampaigns(days);

  const chartData = useMemo(() => {
    if (!topPerformingCampaignsData?.data) return [];
    return [...topPerformingCampaignsData.data]
      .map((c) => ({
        name: c.campaignName ?? "Untitled",
        sent: c.total_sent ?? 0,
        opened: c.total_opened ?? 0,
        replied: c.total_replied ?? 0,
        clicked: c.total_clicked ?? 0,
        contacts: c.total_contacts ?? 0,
      }))
      .reverse();
  }, [topPerformingCampaignsData]);

  const hasAnyValue = useMemo(
    () => chartData.some((d) => d.sent > 0 || d.opened > 0 || d.replied > 0),
    [chartData],
  );

  // Dynamic width: ~6.5px per char, capped between 80px and 160px
  const yAxisWidth = useMemo(() => {
    if (!chartData.length) return 100;
    const longest = Math.max(...chartData.map((d) => d.name.length));
    return Math.min(Math.max(Math.round(longest * 6.5) + 16, 80), 160);
  }, [chartData]);

  const domainMax = computeDomainMax(chartData);
  const chartHeight = Math.max(chartData.length * 68, 120);

  if (!chartData.length || !hasAnyValue) {
    return (
      <div className="w-full flex flex-col h-full items-center justify-center gap-2 py-12">
        <p className="text-sm font-medium text-gray-600">
          No data to display yet
        </p>
        <p className="text-xs text-gray-400 text-center max-w-xs">
          Run campaigns and capture events — their performance will appear here.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col">
      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          layout="vertical"
          data={chartData}
          margin={{ right: 20, bottom: 0, left: 0 }}
          barSize={32}
          barCategoryGap={0}
          barGap={8}
        >
          <CartesianGrid
            horizontal={false}
            vertical={true}
            stroke="#E5E7EB"
            strokeOpacity={0.5}
          />

          <XAxis
            type="number"
            domain={[0, domainMax]}
            tickCount={6}
            tickLine={false}
            axisLine={false}
            tick={{
              fill: "#535862",
              fontSize: 12,
              fontFamily: "Inter, system-ui, sans-serif",
            }}
          />

          <YAxis
            type="category"
            dataKey="name"
            width={yAxisWidth}
            tick={
              <CustomYAxisTick maxChars={Math.floor((yAxisWidth - 16) / 6.5)} />
            }
            tickLine={false}
            axisLine={false}
          />

          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: "rgba(0, 0, 0, 0.04)" }}
          />

          <Bar dataKey="replied" stackId="a" fill="#207C97" name="Replied" />
          <Bar dataKey="opened" stackId="a" fill="#63A3B6" name="Opened" />
          <Bar dataKey="sent" stackId="a" fill="#A6CBD5" name="Sent" />
        </BarChart>
      </ResponsiveContainer>

      <div className="flex justify-center w-full gap-6 text-xs text-gray-500">
        {[
          { color: "#A6CBD5", label: "Sent" },
          { color: "#63A3B6", label: "Opened" },
          { color: "#207C97", label: "Replied" },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5">
            <span
              className="inline-block rounded-full"
              style={{
                width: 10,
                height: 10,
                background: color,
                flexShrink: 0,
              }}
            />
            <span>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
